import { giphyBlack } from '@giphy/colors'
import { uniqueId } from 'lodash'
import { useContext } from 'react'
import { UGEContext } from '../../context'
import { UGELayer, UGELayerAction, ugeLayerActions } from '../../types'
import {
    gaValueFromLayerAction,
    gradientFromLayerAction,
    iconFromLayerAction,
    labelFromLayerAction,
} from '../../utilities/layer-action'
import Attribution from './attribution'
import { Menu, MenuItem, MenuItemIcon, MenuItemLabel } from './style'

type Props = {
    className?: string
    layer: UGELayer
    onAfterAction?: () => void
}

const UGEArtboardContextMenu = ({ className, layer, onAfterAction }: Props) => {
    const {
        addLayer,
        allowedEditors,
        layerActionLabels,
        layers,
        openEditor,
        removeLayerById,
        updateLayerById,
        setLayers,
        trackGAEvent,
    } = useContext(UGEContext)
    const canEditTiming = allowedEditors.includes('layers')
    const canSearchStickers = allowedEditors.includes('stickers')
    const singleStickerMode = allowedEditors.length === 1 && allowedEditors[0] === 'stickers'
    const visibleLayerActions = singleStickerMode
        ? ugeLayerActions.filter((action) => action !== 'duplicate' && action !== 'mirror')
        : ugeLayerActions

    function triggerGAEvent(value: string) {
        trackGAEvent('creation_asset_menu_option', {
            content: {
                contentId: layer.gifId,
                contentType: layer.type === 'image' ? undefined : layer.type,
            },
            creation: {
                contentEditAction: value,
            },
        })
    }

    function onItemClick(action: UGELayerAction) {
        switch (action) {
            case 'delete':
                removeLayerById(layer.id)
                break
            case 'duplicate':
                addLayer({ ...layer, id: uniqueId(`uge-duplicate-`) })
                break
            case 'mirror':
                updateLayerById(layer.id, {
                    mirrored: layer.props.mirrored ? false : true,
                })
                break
            case 'send-to-back':
                setLayers([layer, ...layers.filter((l) => l.id !== layer.id)])
                break
        }

        onAfterAction?.()
        triggerGAEvent(gaValueFromLayerAction(action))
    }

    function onTimingClick() {
        openEditor('layers', { layerId: layer.id })
        onAfterAction?.()
        triggerGAEvent('timing')
    }

    function onStickerSearch(searchTerm?: string) {
        openEditor('stickers', { searchTerm })
        onAfterAction?.()
        triggerGAEvent('search')
    }

    return (
        <div className={className}>
            {layer.user && canSearchStickers && !singleStickerMode && (
                <Attribution user={layer.user} onSearch={onStickerSearch} />
            )}
            <Menu>
                {canEditTiming && (
                    <MenuItem onClick={() => onTimingClick()}>
                        <MenuItemLabel color={giphyBlack} size="medium">
                            Timing
                        </MenuItemLabel>
                        <MenuItemIcon gradient="blue-green" icon="clock" inverted size="medium" />
                    </MenuItem>
                )}
                {visibleLayerActions.map((action) => {
                    const gradient = gradientFromLayerAction(action)
                    const icon = iconFromLayerAction(action)
                    const label = labelFromLayerAction(action, layerActionLabels)

                    // hide send to back if layer is already in the back
                    if (action === 'send-to-back' && layers.indexOf(layer) === 0) {
                        return null
                    }

                    return (
                        <MenuItem key={`uge-context-menu-${action}`} onClick={() => onItemClick(action)}>
                            <MenuItemLabel color={giphyBlack} size="medium">
                                {label}
                            </MenuItemLabel>
                            <MenuItemIcon gradient={gradient} icon={icon} inverted size="medium" />
                        </MenuItem>
                    )
                })}
            </Menu>
        </div>
    )
}

export default UGEArtboardContextMenu
